import React from 'react';
import { Box } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const FilterPills = ({ 
  filters = {}, 
  onRemoveFilter,
  onClearAll,
  columnConfigs = {}
}) => {
  const { t } = useTranslation();
  
  // Convert filters object to array of pills
  const { i18n } = useTranslation();
  const locale = { es: 'es-ES', en: 'en-US', it: 'it-IT' }[i18n.language] || 'es-ES';

  const filterPills = React.useMemo(() => {
    const pills = [];

    Object.entries(filters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;
      // Skip non-array metadata keys like dateFilterMode
      if (typeof values === 'string') return;

      const columnConfig = columnConfigs[columnKey];
      const columnTitle = columnConfig?.title || columnKey;
      const valueFormatter = columnConfig?.valueFormatter;

      // Special handling for date range
      if (columnKey === 'dynamicDateRange') {
        if (Array.isArray(values) && values.length === 2) {
          const isStartDateMode = filters.dateFilterMode === 'startDate';
          let label;
          if (isStartDateMode) {
            const parts = values[0].split('/');
            if (parts.length === 3) {
              const date = new Date(parts[2], parts[1] - 1, parts[0]);
              const monthName = date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
              label = `${t('experiments_filterByStartDate')}: ${monthName.charAt(0).toUpperCase() + monthName.slice(1)}`;
            } else {
              label = `${t('experiments_filterByStartDate')}: ${values[0]}`;
            }
          } else {
            label = `${columnTitle}: ${values[0]} - ${values[1]}`;
          }
          pills.push({
            id: `${columnKey}-${values[0]}-${values[1]}`,
            columnKey,
            value: values,
            label
          });
        }
      } else {
        values.forEach(value => {
          const displayValue = typeof valueFormatter === 'function' ? valueFormatter(value) : value;
          pills.push({
            id: `${columnKey}-${value}`,
            columnKey,
            value,
            label: `${columnTitle}: ${displayValue}`
          });
        });
      }
    });
    
    return pills;
  }, [filters, columnConfigs]);

  return (
    <Box sx={{ 
      display: 'flex',
      flexWrap: 'wrap',
      gap: 1,
      alignItems: 'center',
      minHeight: '32px', // Ensure consistent height even when empty
      py: 0.5 // Small padding to maintain spacing
    }}>
      {filterPills.map(pill => (
        <Box
          key={pill.id}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            height: '28px',
            padding: '0 12px',
            backgroundColor: '#e7f3ff',
            border: '1px solid #cce5ff',
            borderRadius: '16px',
            gap: 0.75,
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: '#dae9ff',
              borderColor: '#b8d9ff',
              transform: 'translateY(-1px)',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
            }
          }}
        >
          <span style={{
            fontSize: '13px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
            fontWeight: 600,
            color: '#0969da',
            letterSpacing: '0.02em'
          }}>{pill.label}</span>
          <Close
            sx={{ 
              fontSize: 16,
              cursor: 'pointer',
              color: '#0969da',
              opacity: 0.7,
              marginLeft: '2px',
              '&:hover': {
                opacity: 1,
              }
            }}
            onClick={() => onRemoveFilter(pill.columnKey, pill.value)}
          />
        </Box>
      ))}
      
      {/* Clear All button - only show when there are multiple active filters */}
      {filterPills.length > 1 && (
        <Box
          onClick={onClearAll}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            height: '28px',
            padding: '0 12px',
            backgroundColor: '#f6f6f7',
            border: '1px solid #d1d9e0',
            borderRadius: '16px',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.06)',
              borderColor: '#a7b0ba',
              transform: 'translateY(-1px)',
            }
          }}
        >
          <span style={{
            fontSize: '13px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
            fontWeight: 500,
            color: '#656d76',
            letterSpacing: '0.02em'
          }}>{t('clearAll') || 'Clear all'}</span>
        </Box>
      )}
    </Box>
  );
};

export default FilterPills;
